<template>
  <div
    class="d-flex align-start flex-nowrap"
    style="gap: 4px 8px;"
  >
    <v-menu
      ref="dateMenu"
      v-model="isDatePickerVisible"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template #activator="{ attrs, on }">
        <v-text-field
          v-model="formattedDate"
          append-icon="mdi-calendar"
          class="flex-grow-1"
          :disabled="disabled"
          :rules="validationRules"
          style="flex-basis: 0; min-width: 150px;"
          v-bind="{ ...$attrs, ...attrs }"
          v-on="on"
          @blur="e => onInputBlur(e)"
          @change="_ => normalizeFormattedDate()"
          @focus="_ => onInputFocus()"
          @keydown.tab="_ => onInputTab()"
        />
      </template>
      <v-date-picker
        v-model="lazyDate"
        @change="_ => onDateSelected()"
      />
    </v-menu>

    <v-menu
      v-if="showAdjustDays && hasValidDate"
      v-model="isAdjustMenuOpen"
      :close-on-content-click="false"
      :disabled="disabled"
      transition="scale-transition"
      offset-y
      nudge-bottom="4"
      min-width="280"
      max-width="320"
    >
      <template #activator="{ attrs: menuAttrs, on: menuOn }">
        <v-tooltip bottom>
          <template #activator="{ attrs: tooltipAttrs, on: tooltipOn }">
            <v-btn
              class="flex-shrink-0"
              color="grey lighten-3"
              depressed
              :disabled="disabled"
              small
              style="margin-top: 14px;"
              v-bind="{ ...menuAttrs, ...tooltipAttrs }"
              v-on="{ ...menuOn, ...tooltipOn }"
              @click.stop
              @mousedown.stop
            >
              <v-icon small>
                mdi-plus-minus
              </v-icon>
            </v-btn>
          </template>
          <span>Adjust date</span>
        </v-tooltip>
      </template>

      <v-card
        class="pa-4"
        width="300"
        style="max-width: calc(100vw - 24px);"
      >
        <span class="text-subtitle-2 font-weight-medium">Adjust date</span>

        <v-btn-toggle
          v-model="adjustOperation"
          class="d-flex mt-3 mb-3"
          color="primary"
          dense
          mandatory
          rounded
        >
          <v-btn
            class="flex-grow-1"
            value="add"
          >
            Add
          </v-btn>
          <v-btn
            class="flex-grow-1"
            value="subtract"
          >
            Subtract
          </v-btn>
        </v-btn-toggle>

        <v-chip-group>
          <v-chip
            v-for="preset of dayPresets"
            :key="preset"
            :color="adjustDays === preset ? 'primary' : undefined"
            label
            :outlined="adjustDays !== preset"
            small
            @click="_ => selectPreset(preset)"
          >
            {{ preset }}d
          </v-chip>
        </v-chip-group>

        <v-text-field
          v-model.number="adjustDays"
          autofocus
          class="mt-2"
          dense
          hide-details="auto"
          label="Custom number of days"
          min="1"
          outlined
          type="number"
          @keydown.enter="_ => applyAdjustment()"
        />

        <v-expand-transition>
          <div
            v-if="adjustedDatePreview"
            class="d-flex align-center mt-4 text-body-2"
            :class="adjustOperation === 'subtract' ? 'orange--text text--darken-3' : 'green--text text--darken-3'"
          >
            <v-icon
              :color="adjustOperation === 'subtract' ? 'orange darken-3' : 'green darken-3'"
              left
              small
            >
              {{ adjustOperation === 'subtract' ? 'mdi-arrow-down-bold-circle-outline' : 'mdi-arrow-up-bold-circle-outline' }}
            </v-icon>
            {{ adjustedDatePreview }}
          </div>
        </v-expand-transition>

        <v-card-actions class="px-0 pb-0">
          <v-spacer />
          <v-btn
            small
            text
            @click="_ => closeAdjustMenu()"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!isAdjustDaysValid"
            small
            text
            @click="_ => applyAdjustment()"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DateField',
  inheritAttrs: false,

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    rules: {
      type: Array,
      default: _ => [],
    },

    showAdjustDays: {
      type: Boolean,
      default: false,
    },

    value: {
      type: String,
      default: '',
    },
  },

  data () {
    return {
      adjustDays: null,
      adjustOperation: 'add',
      dayPresets: [7, 14, 30, 60, 90],
      formattedDate: '',
      isAdjustMenuOpen: false,
      isDatePickerVisible: false,
      lazyDate: null,
      validationRules: [
        ...this.rules,
        v => moment(v, 'MM/DD/YYYY').isValid() || 'Invalid date',
      ],
    }
  },

  computed: {
    adjustMethod () {
      return this.adjustOperation === 'subtract' ? 'subtract' : 'add'
    },

    adjustedDatePreview () {
      if (!this.isAdjustDaysValid) return null

      const newDate = moment(this.lazyDate)[this.adjustMethod](this.adjustDays, 'days')
      return newDate.format('ddd, MMM D, YYYY')
    },

    hasValidDate () {
      return !!this.lazyDate && moment(this.lazyDate).isValid()
    },

    isAdjustDaysValid () {
      return Number.isInteger(this.adjustDays) && this.adjustDays > 0
    },
  },

  watch: {
    isAdjustMenuOpen (isOpen) {
      if (isOpen) return

      this.adjustDays = null
      this.adjustOperation = 'add'
    },

    value: {
      handler (val) {
        const newDate = moment(val)

        if (newDate.isValid()) {
          this.lazyDate = newDate.format('YYYY-MM-DD')
          this.updateFormattedDate()
        } else {
          this.lazyDate = null
          this.formattedDate = val
        }
      },

      immediate: true,
    },
  },

  methods: {
    applyAdjustment () {
      if (!this.isAdjustDaysValid) return

      const newDate = moment(this.lazyDate)[this.adjustMethod](this.adjustDays, 'days')
      this.lazyDate = newDate.format('YYYY-MM-DD')
      this.updateFormattedDate()
      this.isAdjustMenuOpen = false
    },

    closeAdjustMenu () {
      this.isAdjustMenuOpen = false
    },

    normalizeFormattedDate () {
      if (!this.formattedDate) return

      const regex = /^([0-9]{1,2})[^0-9]{0,1}([0-9]{1,2})[^0-9]{0,1}([0-9]{2,4})$/
      const digits = this.formattedDate.match(regex)?.slice(1)?.join('/')
      const date = moment(digits, 'MM/DD/YYYY')

      if (date.isValid()) this.formattedDate = date.format('L')
      this.updateDateValue()
    },

    onDateSelected () {
      this.isDatePickerVisible = false
      this.updateFormattedDate()
    },

    onInputBlur (e) {
      // Typing a date and clicking away (rather than picking a day on the calendar
      // or pressing Tab) previously left the calendar popover open indefinitely,
      // which then swallowed the next click meant for the adjust-date button.
      //
      // Clicking a day in the calendar also blurs this input: focus moves to the
      // clicked day button on mousedown, before the date-picker's own click
      // handler (onDateSelected) runs. On a real (non-instant) click there's a
      // real gap between that mousedown/blur and the mouseup/click that actually
      // picks the date, so normalizing here would re-parse the *old* text and
      // clobber the date the user is in the middle of picking.
      //
      // If we can tell focus is headed into our own calendar popover, skip
      // entirely and let onDateSelected handle it. Otherwise defer to the next
      // tick so a same-tick pick (already handled by onDateSelected, which
      // clears isDatePickerVisible) still wins over this stale normalize.
      const menuContent = this.$refs.dateMenu?.$refs?.content
      if (menuContent && e.relatedTarget && menuContent.contains(e.relatedTarget)) return

      setTimeout(_ => {
        if (!this.isDatePickerVisible) return
        this.isDatePickerVisible = false
        this.normalizeFormattedDate()
      }, 0)
    },

    onInputFocus () {
      setTimeout(_ => { this.isDatePickerVisible = true }, 100)
    },

    onInputTab () {
      this.isDatePickerVisible = false
      this.normalizeFormattedDate()
    },

    selectPreset (preset) {
      this.adjustDays = preset
    },

    updateDateValue () {
      const date = moment(this.formattedDate, 'L')
      if (date.isValid()) {
        this.lazyDate = date.format('YYYY-MM-DD')
        this.$emit('input', this.lazyDate)
      } else {
        this.$emit('input', '')
      }
    },

    updateFormattedDate () {
      const date = moment(this.lazyDate)

      if (date.isValid()) {
        this.formattedDate = date.format('L')
      } else {
        this.formattedDate = this.lazyDate || ''
        this.lazyDate = null
      }

      this.$emit('input', this.lazyDate)
    },
  },
}
</script>
